const safe = (x) => Number(x) || 0;

// --- 0️⃣ INITIAL TOTALS ---
export function initialTotals() {
  return {
    // Raw totals
    two_fg_attempts: 0,
    two_fg_made: 0,
    three_fg_attempts: 0,
    three_fg_made: 0,
    ft_attempts: 0,
    ft_made: 0,
    off_rebounds: 0,
    def_rebounds: 0,
    treb: 0,
    assists: 0,
    steals: 0,
    blocks: 0,
    turnovers: 0,
    fouls: 0,
    minutes: 0,
    bpm: 0,

    // Computed totals
    fg_attempts: 0,
    fg_made: 0,
    total_points: 0,
    minutes_formatted: "0:00",

    // Percentages
    fg_percentage: 0,
    three_pt_percentage: 0,
    ft_percentage: 0,
    efg_percentage: 0,
    ts_percentage: 0,
    tov_percentage: 0,

    // Advanced
    usg_percentage: 0,
    off_rating: 0,
    def_rating: 0,
    pm: 0,

    // Per-40
    per40: {},
  };
}

// --- 1️⃣ RAW totals ---
function accumulateRawTotals(playerGames) {
  const totals = initialTotals();

  playerGames.forEach((g) => {
    totals.two_fg_attempts += safe(g.two_fg_attempts);
    totals.two_fg_made += safe(g.two_fg_made);
    totals.three_fg_attempts += safe(g.three_fg_attempts);
    totals.three_fg_made += safe(g.three_fg_made);
    totals.ft_attempts += safe(g.ft_attempts);
    totals.ft_made += safe(g.ft_made);
    totals.off_rebounds += safe(g.off_rebounds);
    totals.def_rebounds += safe(g.def_rebounds);
    totals.treb += safe(g.off_rebounds) + safe(g.def_rebounds);
    totals.assists += safe(g.assists);
    totals.steals += safe(g.steals);
    totals.blocks += safe(g.blocks);
    totals.turnovers += safe(g.turnovers);
    totals.fouls += safe(g.fouls);
    totals.minutes += safe(g.minutes);
    totals.bpm += safe(g.bpm);
  });

  return totals;
}

// --- 2️⃣ BASIC totals ---
function computeBasicTotals(totals) {
  totals.fg_attempts = totals.two_fg_attempts + totals.three_fg_attempts;
  totals.fg_made = totals.two_fg_made + totals.three_fg_made;
  totals.total_points =
    totals.two_fg_made * 2 +
    totals.three_fg_made * 3 +
    totals.ft_made;

  return totals;
}

// --- 3️⃣ PERCENTAGES ---
function computePercentages(totals) {
  totals.fg_percentage =
    totals.fg_attempts > 0
      ? Number(((totals.fg_made / totals.fg_attempts) * 100).toFixed(1))
      : 0;

  totals.three_pt_percentage =
    totals.three_fg_attempts > 0
      ? Number(((totals.three_fg_made / totals.three_fg_attempts) * 100).toFixed(1))
      : 0;

  totals.ft_percentage =
    totals.ft_attempts > 0
      ? Number(((totals.ft_made / totals.ft_attempts) * 100).toFixed(1))
      : 0;

  totals.efg_percentage =
    totals.fg_attempts > 0
      ? Number(
          (((totals.fg_made + 0.5 * totals.three_fg_made) / totals.fg_attempts) * 100).toFixed(1)
        )
      : 0;

  totals.ts_percentage =
    totals.fg_attempts + 0.44 * totals.ft_attempts > 0
      ? Number(
          ((totals.total_points / (2 * (totals.fg_attempts + 0.44 * totals.ft_attempts))) * 100).toFixed(1)
        )
      : 0;

  totals.tov_percentage =
    totals.fg_attempts + 0.44 * totals.ft_attempts + totals.turnovers > 0
      ? Number(
          ((totals.turnovers / (totals.fg_attempts + 0.44 * totals.ft_attempts + totals.turnovers)) * 100).toFixed(1)
        )
      : 0;

  return totals;
}

// --- 4️⃣ TEAM TOTALS CUMULATIVE ---
function normalizeTeamTotals(t = {}) {
  // Handles both old and new team totals format
  const twoA = safe(t.two_fg_attempts);
  const threeA = safe(t.three_fg_attempts);
  return {
    fg_attempts: safe(t.fg_attempts) + twoA + threeA,
    ft_attempts: safe(t.ft_attempts),
    turnovers: safe(t.turnovers),
    minutes: safe(t.minutes),
  };
}

function computeCumulativeTeamTotals(playerGames, teamTotalsByGame) {
  if (!teamTotalsByGame) return null;

  const teamTotals = { fg_attempts: 0, ft_attempts: 0, turnovers: 0, minutes: 0 };

  playerGames.forEach((g) => {
    const t = teamTotalsByGame[g.game_id];
    if (t) {
      const norm = normalizeTeamTotals(t);
      teamTotals.fg_attempts += norm.fg_attempts;
      teamTotals.ft_attempts += norm.ft_attempts;
      teamTotals.turnovers += norm.turnovers;
      teamTotals.minutes += norm.minutes;
    }
  });

  return teamTotals;
}

// --- 5️⃣ ADVANCED ---
function computeAdvanced(totals, teamTotals, gamesPlayed) {
  const playerMinutes = totals.minutes / 60;
  const playerPossessions = totals.fg_attempts + 0.44 * totals.ft_attempts + totals.turnovers;

  if (teamTotals && teamTotals.minutes > 0) {
    const teamMinutes = teamTotals.minutes / 60;
    const teamPossessions =
      teamTotals.fg_attempts + 0.44 * teamTotals.ft_attempts + teamTotals.turnovers;

    totals.usg_percentage =
      teamPossessions > 0 && playerMinutes > 0
        ? Number(
            (
              100 *
              (playerPossessions * (teamMinutes / 5)) /
              (playerMinutes * teamPossessions)
            ).toFixed(1)
          )
        : 0;

    totals.off_rating =
      playerPossessions > 0
        ? Number(((totals.total_points / playerPossessions) * 100).toFixed(1))
        : 0;

    const pmPer40 =
      playerMinutes > 0
        ? (totals.bpm / gamesPlayed) * (40 / playerMinutes)
        : 0;

    totals.def_rating = Number((100 - pmPer40).toFixed(1));
  } else {
    totals.off_rating =
      playerPossessions > 0
        ? Number(((totals.total_points / playerPossessions) * 100).toFixed(1))
        : 0;

    totals.usg_percentage =
      playerMinutes > 0
        ? Number((100 * (playerPossessions / playerMinutes)).toFixed(1))
        : 0;

    totals.def_rating = 0;
  }

  totals.pm = Number((totals.bpm / gamesPlayed).toFixed(1));

  return totals;
}

// --- 6️⃣ PER-40 ---
function computePer40(totals) {
  const minutes = totals.minutes / 60;
  if (!minutes) return totals;

  const f = 40 / minutes;

  totals.per40 = {
    points: Number((totals.total_points * f).toFixed(1)),
    two_fg_attempts: Number((totals.two_fg_attempts * f).toFixed(1)),
    two_fg_made: Number((totals.two_fg_made * f).toFixed(1)),
    three_fg_attempts: Number((totals.three_fg_attempts * f).toFixed(1)),
    three_fg_made: Number((totals.three_fg_made * f).toFixed(1)),
    ft_attempts: Number((totals.ft_attempts * f).toFixed(1)),
    ft_made: Number((totals.ft_made * f).toFixed(1)),
    fg_attempts: Number((totals.fg_attempts * f).toFixed(1)),
    fg_made: Number((totals.fg_made * f).toFixed(1)),
    rebounds: Number((totals.treb * f).toFixed(1)),
    off_rebounds: Number((totals.off_rebounds * f).toFixed(1)),
    def_rebounds: Number((totals.def_rebounds * f).toFixed(1)),
    assists: Number((totals.assists * f).toFixed(1)),
    steals: Number((totals.steals * f).toFixed(1)),
    blocks: Number((totals.blocks * f).toFixed(1)),
    turnovers: Number((totals.turnovers * f).toFixed(1)),
    fouls: Number((totals.fouls * f).toFixed(1)),
  };

  return totals;
}

// --- ORCHESTRATOR ---
export function calculateTotals(playerGames, teamTotalsByGame = null) {
  if (!playerGames || !playerGames.length) return initialTotals();

  const gamesPlayed = playerGames.length;

  // 1️⃣ Raw totals
  let totals = accumulateRawTotals(playerGames);

  // 2️⃣ Basic totals
  totals = computeBasicTotals(totals);

  // 3️⃣ Percentages
  totals = computePercentages(totals);

  // 4️⃣ Compute cumulative team totals for advanced stats
  const teamTotals = computeCumulativeTeamTotals(playerGames, teamTotalsByGame);

  // 5️⃣ Advanced stats
  totals = computeAdvanced(totals, teamTotals, gamesPlayed);

  // 6️⃣ Per-40 stats
  totals = computePer40(totals);

  // Format minutes
  totals.minutes_formatted = formatTime(totals.minutes);

  return totals;
}

// --- HELPER ---
export function formatTime(seconds) {
  if (!seconds || seconds <= 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}
